import { useEffect, useState } from 'react';
import {
    Button, Card, Input, Label, ListBox, ListBoxItem, Modal, Select, TextField
} from '@heroui/react';

import Notice from '../../common/Notice';
import ViewTab from '../../common/ViewTab';
import TimeAgo from '../../common/TimeAgo';
import StatusChip from '../../common/StatusChip';
import { LoadMore } from '../../common/LoadMore';
import { SkeletonText } from '../../common/Skeleton';
import apiKeysService from '../../../services/apiKeys.service';
import { API_KEYS, messageOf, SELECT_PLACEHOLDER } from '../../../i18n/el';

// Οθόνη διαχείρισης ανακλητών κλειδιών (ΜΛΑ-08, §4.3.5).
//
// Ο μηχανισμός υπήρχε από το P0-4 και δούλευε· έλειπε ο τρόπος να δημιουργηθεί ή
// να ανακληθεί κλειδί χωρίς απευθείας εγγραφή στη βάση. Η ΜΛΑ-08 ζητά ρητά
// «δυνατότητα ανάκλησης», και δυνατότητα που απαιτεί πρόσβαση στο Atlas δεν είναι
// δυνατότητα του συστήματος: είναι η κατηγορία Α του Κεφαλαίου 6, λειτουργία που
// υπάρχει στο API και δεν φτάνει στον χρήστη.
//
// Η οθόνη είναι ορατή μόνο σε διαχειριστή, αλλά ο πραγματικός φράχτης είναι το
// API: το `integration:manage` του Πίνακα 1 ελέγχεται στον εξυπηρετητή, και η
// απόκρυψη της εγγραφής στο μενού είναι ευχρηστία, όχι ασφάλεια.

export default function ApiKeysPage() {
    const [keys, setKeys] = useState([]);
    const [meta, setMeta] = useState(null);
    const [collections, setCollections] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [label, setLabel] = useState('');
    const [collection, setCollection] = useState('');
    const [isCreating, setCreating] = useState(false);

    // Το μυστικό ζει **μόνο εδώ, μόνο σε αυτή τη μεταβλητή, μόνο μέχρι την επόμενη
    // φόρτωση**. Δεν αποθηκεύεται πουθενά στον πελάτη: ο εξυπηρετητής δεν μπορεί να
    // το ξαναδώσει, και ένα αντίγραφο σε localStorage θα ήταν δεύτερο σημείο
    // διαρροής για κάτι που υπάρχει ήδη μία φορά παραπάνω από όσο πρέπει.
    const [freshSecret, setFreshSecret] = useState(null);
    const [copied, setCopied] = useState(false);

    const [toRevoke, setToRevoke] = useState(null);
    const [toDelete, setToDelete] = useState(null);

    // Η προβολή είναι **φίλτρο του φορτωμένου καταλόγου** και όχι δεύτερο αίτημα.
    // Ο εξυπηρετητής επιστρέφει και τα δύο είδη σε μία σελίδα, και η σελιδοποίηση
    // είναι ενιαία: δύο ερωτήματα με χωριστούς μετρητές θα έδιναν «Φόρτωση κι
    // άλλων» που φορτώνει σελίδα ενεργών ενώ ο χρήστης βλέπει τα ανακλημένα.
    const [view, setView] = useState('active');

    function load(page = 1) {
        setLoading(true);
        return apiKeysService.getApiKeys({ page }).then(
            ({ data, meta: pageMeta }) => {
                setKeys((current) => (page === 1 ? data : [...current, ...data]));
                setMeta(pageMeta);
                setLoading(false);
            },
            (apiError) => {
                setError(messageOf(apiError));
                setLoading(false);
            }
        );
    }

    useEffect(() => {
        load();
        apiKeysService.getCollections().then(
            ({ data }) => setCollections(data),
            () => {}
        );
    }, []);

    function create(event) {
        event.preventDefault();
        if (!label.trim() || !collection) return;

        setCreating(true);
        setError('');
        setCopied(false);

        apiKeysService.createApiKey({ label: label.trim(), collection }).then(
            (created) => {
                setFreshSecret(created.key);
                setLabel('');
                setCreating(false);
                load();
            },
            (apiError) => {
                setError(messageOf(apiError));
                setCreating(false);
            }
        );
    }

    function revoke() {
        const target = toRevoke;
        setToRevoke(null);
        setError('');

        apiKeysService.revokeApiKey(target.id).then(
            () => load(),
            (apiError) => setError(messageOf(apiError))
        );
    }

    const active = keys.filter((key) => !key.revoked);
    const revoked = keys.filter((key) => key.revoked);
    const shown = view === 'revoked' ? revoked : active;

    // **D24.** Η διαγραφή είναι δεύτερο, ρητό βήμα και μόνο για ανακλημένα: το API
    // απαντά 409 σε ενεργό κλειδί. Ο κατάλογος δείχνει το κουμπί μόνο στην καρτέλα
    // των ανακλημένων, αλλά ο φράχτης είναι ο εξυπηρετητής και όχι η απόκρυψη.
    function destroy() {
        const target = toDelete;
        setToDelete(null);
        setError('');

        apiKeysService.deleteApiKey(target.id).then(
            () => load(),
            (apiError) => setError(messageOf(apiError))
        );
    }

    function copySecret() {
        navigator.clipboard?.writeText(freshSecret);
        setCopied(true);
    }

    return (
        <div className="flex flex-col gap-6">
            {error && <Notice status="danger" title={error} />}

            <Card>
                <Card.Header>
                    <Card.Title>{API_KEYS.title}</Card.Title>
                </Card.Header>
                <Card.Content className="flex flex-col gap-4">
                    <p className="text-sm text-muted">{API_KEYS.intro}</p>

                    <form onSubmit={create} className="flex flex-col gap-4">
                        <TextField value={label} onChange={setLabel}>
                            <Label>{API_KEYS.label}</Label>
                            <Input placeholder={API_KEYS.labelHint} />
                        </TextField>

                        <Select placeholder={SELECT_PLACEHOLDER}
                            selectedKey={collection}
                            onSelectionChange={(key) => setCollection(String(key))}
                        >
                            <Label>{API_KEYS.collection}</Label>
                            <Select.Trigger>
                                <Select.Value />
                                <Select.Indicator />
                            </Select.Trigger>
                            <Select.Popover>
                                <ListBox items={collections}>
                                    {(item) => <ListBoxItem id={item.id}>{item.title}</ListBoxItem>}
                                </ListBox>
                            </Select.Popover>
                        </Select>
                        <p className="text-xs text-muted">{API_KEYS.collectionHint}</p>

                        <Button
                            type="submit"
                            isPending={isCreating}
                            isDisabled={!label.trim() || !collection}
                            className="self-start"
                        >
                            {isCreating ? API_KEYS.creating : API_KEYS.create}
                        </Button>
                    </form>
                </Card.Content>
            </Card>

            {/* Η μοναδική εμφάνιση του μυστικού. Δεν κλείνει με κουμπί «κατάλαβα»:
                φεύγει μόνο με την επόμενη δημιουργία ή με φυγή από την οθόνη, ώστε
                να μην εξαφανιστεί κατά λάθος πριν αντιγραφεί. */}
            {freshSecret && (
                <Notice status="warning" title={API_KEYS.secretTitle}>
                    <div className="flex flex-col gap-3">
                        <code className="block overflow-x-auto rounded border border-border bg-surface px-3 py-2 text-xs">
                            {freshSecret}
                        </code>
                        <div className="flex items-center gap-3">
                            <Button variant="outline" onPress={copySecret}>
                                <i className="fa-solid fa-copy" aria-hidden="true" />
                                {copied ? API_KEYS.copied : API_KEYS.copy}
                            </Button>
                            <span className="text-xs">{API_KEYS.secretWarning}</span>
                        </div>
                    </div>
                </Notice>
            )}

            <Card>
                <Card.Header>
                    <Card.Title>{API_KEYS.listTitle}</Card.Title>
                </Card.Header>
                <Card.Content className="flex flex-col gap-4">
                    {isLoading && keys.length === 0 && (
                        <>
                            <SkeletonText />
                            <SkeletonText width="70%" />
                        </>
                    )}

                    {!isLoading && !error && keys.length === 0 && (
                        <p className="text-sm text-muted">{API_KEYS.empty}</p>
                    )}

                    {keys.length > 0 && (
                        <div className="flex flex-wrap gap-2" role="group" aria-label={API_KEYS.viewLabel}>
                            <ViewTab
                                icon="fa-solid fa-key"
                                isActive={view === 'active'}
                                onPress={() => setView('active')}
                            >
                                {API_KEYS.viewActive} ({active.length})
                            </ViewTab>
                            <ViewTab
                                icon="fa-solid fa-ban"
                                isActive={view === 'revoked'}
                                onPress={() => setView('revoked')}
                            >
                                {API_KEYS.viewRevoked} ({revoked.length})
                            </ViewTab>
                        </div>
                    )}

                    {/* Κενή καρτέλα και κενός κατάλογος είναι **διαφορετικές
                        καταστάσεις** και δεν μοιράζονται μήνυμα: «δεν υπάρχουν
                        κλειδιά» σε μια οθόνη που δείχνει τρία ανακλημένα είναι
                        ψευδές. */}
                    {!isLoading && keys.length > 0 && shown.length === 0 && (
                        <p className="text-sm text-muted">
                            {view === 'revoked' ? API_KEYS.emptyRevoked : API_KEYS.emptyActive}
                        </p>
                    )}

                    <ul className="flex flex-col gap-4">
                        {shown.map((key) => (
                            <li key={key.id} className="flex flex-wrap items-baseline justify-between gap-3">
                                <div className="flex min-w-0 flex-col gap-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-medium">{key.label}</span>
                                        {key.revoked ? (
                                            <StatusChip icon="fa-solid fa-ban" tone="neutral">
                                                {API_KEYS.revoked}
                                            </StatusChip>
                                        ) : (
                                            <StatusChip icon="fa-solid fa-key" tone="teal">
                                                {API_KEYS.active}
                                            </StatusChip>
                                        )}
                                        <StatusChip icon="fa-solid fa-layer-group" tone="purple">
                                            {key.collection?.title ?? '—'}
                                        </StatusChip>
                                    </div>
                                    <p className="text-xs text-muted">
                                        {API_KEYS.createdAt}: <TimeAgo date={key.createdAt} /> ·{' '}
                                        {key.lastUsedAt ? (
                                            <>
                                                {API_KEYS.lastUsedAt}: <TimeAgo date={key.lastUsedAt} />
                                            </>
                                        ) : (
                                            API_KEYS.neverUsed
                                        )}
                                    </p>
                                </div>

                                {key.revoked ? (
                                    <Button variant="outline" onPress={() => setToDelete(key)}>
                                        {API_KEYS.delete}
                                    </Button>
                                ) : (
                                    <Button variant="outline" onPress={() => setToRevoke(key)}>
                                        {API_KEYS.revoke}
                                    </Button>
                                )}
                            </li>
                        ))}
                    </ul>

                    {/* Το `loaded` είναι το πλήθος **όλου** του φορτωμένου καταλόγου
                        και όχι της καρτέλας: το `meta.total` του εξυπηρετητή μετρά
                        κι εκείνο και τα δύο είδη. Δύο μεγέθη από διαφορετικά σύμπαντα
                        στην ίδια πρόταση θα έλεγαν «3 από 11» με έντεκα φορτωμένα. */}
                    <LoadMore
                        loaded={keys.length}
                        meta={meta}
                        isLoading={isLoading}
                        onLoadMore={() => load((meta?.page ?? 1) + 1)}
                        noun={API_KEYS.keysNoun}
                    />
                </Card.Content>
            </Card>

            {/* ΜΛΑ-03: η επιβεβαίωση **ονομάζει τι χάνεται, με την ημερομηνία**. Το
                ιστορικό χρήσης είναι ΕΝΑ πεδίο πάνω σε αυτό το έγγραφο — υψηλή
                στάθμη και όχι ημερολόγιο — και η διαγραφή το σβήνει από τη βάση.
                Επιβιώνει μόνο στο ίχνος δραστηριότητας, που γράφεται πρώτο. */}
            {/* ⚠ **Υπό συνθήκη, και είναι διόρθωση ελαττώματος.** Το `toDelete`
                μηδενίζεται μέσα στη `destroy`, δηλαδή **μετά** από επιτυχή διαγραφή
                και όχι από το κλείσιμο του διαλόγου· το `Modal.Root` του HeroUI
                κλείνει μόνο όταν το κλείσιμο το ξεκινά το ίδιο. Στην πιο
                καταστροφική οθόνη του συστήματος, ο διάλογος έμενε ανοιχτός πάνω
                από κλειδί που είχε ήδη σβήσει. */}
            {toDelete !== null && (
            <Modal.Root isOpen onOpenChange={() => setToDelete(null)}>
                <Modal.Backdrop>
                    <Modal.Container size="sm">
                        <Modal.Dialog>
                            <Modal.Header>
                                <Modal.Heading>{API_KEYS.deleteTitle}</Modal.Heading>
                                <Modal.CloseTrigger />
                            </Modal.Header>
                            <Modal.Body className="flex flex-col gap-3">
                                <p className="text-sm">{toDelete?.label}</p>
                                <Notice status="warning" title={API_KEYS.deleteWarning}>
                                    <p className="text-sm">
                                        {toDelete?.lastUsedAt ? (
                                            <>
                                                {API_KEYS.deleteLoses}{' '}
                                                <TimeAgo date={toDelete.lastUsedAt} />
                                            </>
                                        ) : (
                                            API_KEYS.deleteLosesNothing
                                        )}
                                    </p>
                                </Notice>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="outline" onPress={() => setToDelete(null)}>
                                    {API_KEYS.cancel}
                                </Button>
                                <Button variant="danger" onPress={destroy}>
                                    {API_KEYS.deleteConfirm}
                                </Button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal.Container>
                </Modal.Backdrop>
            </Modal.Root>
            )}

            {/* ⚠ Υπό συνθήκη, για τον ίδιο λόγο: το `toRevoke` μηδενίζεται μέσα στη
                `revoke`, μετά από επιτυχή ανάκληση. */}
            {toRevoke !== null && (
            <Modal.Root isOpen onOpenChange={() => setToRevoke(null)}>
                <Modal.Backdrop>
                    <Modal.Container>
                        <Modal.Dialog>
                            <Modal.Header>
                                {/* `Modal.Heading` και ΟΧΙ `Modal.Title`: το δεύτερο δεν
                                    υπάρχει στη βιβλιοθήκη. Η `Card` έχει `Title`, το `Modal`
                                    έχει `Heading` — και η πρόσβαση σε ανύπαρκτο υποσυστατικό
                                    δίνει `undefined`, που είναι έγκυρη έκφραση: λευκή οθόνη
                                    τη στιγμή που ο διαχειριστής πατούσε «Ανάκληση», με καθαρό
                                    build και πράσινη σουίτα.

                                    **Το ίδιο ελάττωμα εντοπίστηκε ανεξάρτητα σε δύο
                                    μηχανήματα** — από χειροκίνητο έλεγχο και από φράχτη — και
                                    οι δύο διορθώσεις συνέπεσαν στη μία λέξη. Ο φράχτης που
                                    το πιάνει είναι το `components/library.test.js`: επιλύει
                                    κάθε σύνθετη ετικέτα έναντι όσων πράγματι εξάγει η
                                    βιβλιοθήκη, χωρίς να αποδίδει τίποτα — γι' αυτό βλέπει και
                                    στοιχεία μέσα σε διάλογο, που η απόδοση σε συμβολοσειρά
                                    δεν φτάνει ποτέ. */}
                                <Modal.Heading>{API_KEYS.revokeTitle}</Modal.Heading>
                            </Modal.Header>
                            <Modal.Body className="flex flex-col gap-3">
                                <p className="text-sm">{toRevoke?.label}</p>
                                <Notice status="warning" title={API_KEYS.revokeWarning} />
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="outline" onPress={() => setToRevoke(null)}>
                                    {API_KEYS.cancel}
                                </Button>
                                <Button variant="danger" onPress={revoke}>
                                    {API_KEYS.revokeConfirm}
                                </Button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal.Container>
                </Modal.Backdrop>
            </Modal.Root>
            )}
        </div>
    );
}
